import { useQuery } from '@tanstack/react-query';
import type { Sector, SpeciesKey } from '@chicare/shared';

import { api } from './api';

/**
 * Query keys and hooks.
 *
 * Kept in one file so the cache is legible: every key is visible together, and
 * an invalidation can be reasoned about without grepping the whole app.
 */

export const queryKeys = {
  // The sector is part of the key, not a filter applied after. Two shelves
  // sharing one cache entry is how a buyer sees animal health under a human
  // heading for the one render before the refetch lands.
  home: (state?: string, sector?: Sector) => ['home', state ?? 'all', sector ?? 'human'] as const,
  config: () => ['system', 'config'] as const,
  categories: () => ['categories'] as const,
  products: (params: Record<string, unknown>) => ['products', params] as const,
  product: (slug: string, state?: string) => ['product', slug, state ?? 'all'] as const,
  pharmacies: (params: Record<string, unknown>) => ['pharmacies', params] as const,
  me: () => ['auth', 'me'] as const,
};

export function useHome(state?: string, sector?: Sector) {
  return useQuery({
    queryKey: queryKeys.home(state, sector),
    queryFn: ({ signal }) => api.home(state, sector, signal),
  });
}

/**
 * Business rules the clients must not hard-code (§11 verbatim copy, §4.7 claim
 * windows, feature flags). Long-lived: these change on a deploy, not per view.
 */
export function useSystemConfig() {
  return useQuery({
    queryKey: queryKeys.config(),
    queryFn: ({ signal }) => api.config(signal),
    staleTime: 15 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

export function useProductSearch(params: {
  q?: string;
  category?: string;
  state?: string;
  sector?: Sector;
  species?: SpeciesKey;
  /** Omitted means `price`, which is what the API defaults to. */
  sort?: 'price' | 'relevance' | 'newest';
  enabled?: boolean;
}) {
  const { enabled = true, ...query } = params;

  return useQuery({
    queryKey: queryKeys.products(query),
    queryFn: ({ signal }) => api.searchProducts(query, signal),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useProduct(slug: string | undefined, state?: string) {
  return useQuery({
    queryKey: queryKeys.product(slug ?? '', state),
    queryFn: ({ signal }) => api.product(slug!, state, signal),
    enabled: Boolean(slug),
  });
}

export function usePharmacies(params: { state?: string; q?: string } = {}) {
  return useQuery({
    queryKey: queryKeys.pharmacies(params),
    queryFn: ({ signal }) => api.pharmacies(params, signal),
  });
}
